import {
  eachDayOfInterval,
  endOfISOWeek,
  endOfMonth,
  format,
  getISOWeek,
  isSameMonth,
  startOfISOWeek,
  startOfMonth,
} from "date-fns";
import { nl } from "date-fns/locale";

import type { CalendarDay } from "../types/api";

export function MonthCalendar({
  month,
  dayMap,
  selection,
  onSelect,
  todayIso,
}: {
  month: Date;
  dayMap: Map<string, CalendarDay>;
  selection: { start: string; end: string } | null;
  onSelect: (date: string, extend: boolean) => void;
  todayIso: string;
}) {
  const intervalStart = startOfISOWeek(startOfMonth(month));
  const intervalEnd = endOfISOWeek(endOfMonth(month));
  const days = eachDayOfInterval({ start: intervalStart, end: intervalEnd });
  const weeks = Array.from({ length: days.length / 7 }, (_, index) =>
    days.slice(index * 7, index * 7 + 7),
  );
  return (
    <article className="month-calendar">
      <h2>{format(month, "MMMM", { locale: nl })}</h2>
      <div className="month-weekdays" aria-hidden="true">
        <span className="week-heading">wk</span>
        {["ma", "di", "wo", "do", "vr", "za", "zo"].map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className="month-days">
        {weeks.map((week) => (
          <div className="month-week-row" key={week[0].toISOString()}>
            <span
              className="week-number"
              title={`Week ${getISOWeek(week[0])}`}
              aria-label={`Week ${getISOWeek(week[0])}`}
            >
              {getISOWeek(week[0])}
            </span>
            {week.map((date) => {
              const iso = format(date, "yyyy-MM-dd");
              const day = dayMap.get(iso);
              const leaveStatus = day?.layers.leave?.[0]?.status;
              const previousDay = dayMap.get(
                format(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1), "yyyy-MM-dd"),
              );
              const nextDay = dayMap.get(
                format(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1), "yyyy-MM-dd"),
              );
              const continuesFromPrevious =
                leaveStatus === previousDay?.layers.leave?.[0]?.status;
              const continuesToNext = leaveStatus === nextDay?.layers.leave?.[0]?.status;
              const hasSchoolHoliday = Boolean(day?.layers.school_holidays?.length);
              const hasBirthday = Boolean(
                day?.layers.events?.some((event) => event.event_type === "birthday"),
              );
              const classes = [
                "month-day",
                !isSameMonth(date, month) ? "outside" : "",
                day?.flags.is_public_holiday ? "is-public-holiday" : "",
                hasSchoolHoliday ? "has-school-holiday" : "",
                leaveStatus ? `leave-status-${leaveStatus}` : "",
                leaveStatus && !continuesFromPrevious ? "leave-run-start" : "",
                leaveStatus && !continuesToNext ? "leave-run-end" : "",
                selection && iso >= selection.start && iso <= selection.end
                  ? "is-selected"
                  : "",
                // Overloopdagen horen bij de buurmaand; markeer vandaag alleen
                // in de maand zelf.
                iso === todayIso && isSameMonth(date, month) ? "is-today" : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <button
                  key={iso}
                  className={classes}
                  onClick={(event) => onSelect(iso, event.shiftKey)}
                  title={dayTooltip(day)}
                  aria-label={`${format(date, "d MMMM yyyy", { locale: nl })}: ${dayTooltip(day)}`}
                >
                  <span className="month-day-number">{format(date, "d")}</span>
                  <span className="day-indicators">
                    {hasBirthday ? (
                      <i className="event-dot birthday" />
                    ) : null}
                    {day?.layers.notes?.length ? (
                      <i className="event-dot note" />
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </article>
  );
}

export function PlannerLegend() {
  return (
    <div className="calendar-legend planner-legend">
      <span>
        <i className="legend-swatch school-ring" /> Schoolvakantie / studiedag
      </span>
      <span>
        <i className="legend-swatch leave-status-draft" /> Concept
      </span>
      <span>
        <i className="legend-swatch leave-status-requested" /> Aangevraagd
      </span>
      <span>
        <i className="legend-swatch leave-status-approved" /> Goedgekeurd
      </span>
      <span>
        <i className="legend-swatch leave-status-needs_info" /> Toelichting nodig
      </span>
      <span>
        <i className="legend-swatch leave-status-rejected" /> Afgewezen
      </span>
      <span>
        <i className="legend-dot holiday" /> Feestdag
      </span>
      <span>
        <i className="legend-dot birthday" /> Verjaardag
      </span>
    </div>
  );
}

export function dayTooltip(day?: CalendarDay) {
  if (!day) return "Geen bijzonderheden";
  return (
    [
      ...(day.employment_schedules?.map(
        (item) => `${item.employment_name}: ${item.scheduled_minutes / 60} uur`,
      ) ?? []),
      ...(day.layers.school_holidays?.map((item) =>
        item.event_type === "study_day"
          ? `Studiedag: ${item.display_name ?? item.name}${partSuffix(item.part)}`
          : item.display_name ?? item.name,
      ) ?? []),
      ...(day.shift_label ? [`Dienst: ${day.shift_label}`] : []),
      ...(day.layers.public_holidays?.map((item) => item.name) ?? []),
      ...(day.layers.leave?.map(
        (item) => `${item.leave_type.name}: ${item.status_label}`,
      ) ?? []),
      ...(day.layers.events?.map((item) => item.title) ?? []),
      ...(day.layers.notes?.map((item) => item.text) ?? []),
    ].join(" · ") || "Geen bijzonderheden"
  );
}

function partSuffix(part?: string) {
  if (part === "morning") return " (ochtend)";
  if (part === "afternoon") return " (middag)";
  return "";
}
